/**
 * City Beauty Static Banner Section - Shopify Compatible JavaScript
 * Handles static banner functionality including lazy loading, animations, and responsive behavior
 */

(function() {
    'use strict';

    // Static banner configuration
    const staticBannerConfig = {
        sectionSelector: '.cb-static-banner',
        bannerSelector: '.cb-static-banner .banner-inner',
        lazyClass: 'lazy',
        loadedClass: 'loaded',
        animateClass: 'animate',
        dataLoader: 'component',
        defaultSettings: {
            title: 'How to Layer Your City Beauty<sup>®</sup> Products',
            subtitle: 'REGIMEN GUIDE',
            description: 'What is the perfect skincare order? Learn everything you need to know about how to most effectively use your products.',
            buttonText: 'LEARN MORE',
            buttonUrl: '/regimen-guide.php',
            backgroundImage: 'https://cdn.citybeauty.com/images/img/background/regimen-banner-d.jpg',
            mobileBackgroundImage: 'https://cdn.citybeauty.com/images/img/background/regimen-banner-m.jpg'
        }
    };

    /**
     * Initialize Static Banner section components
     */
    function initCityBeautyStaticBannerComponents() {
        // console.log('Initializing City Beauty Static Banner components...');
        
        // Initialize lazy loading
        initLazyLoading();
        
        // Initialize animations
        initAnimations();
        
        // Initialize responsive behavior
        handleStaticBannerResize();
        
        // Initialize background images
        initBackgroundImages();
        
        // console.log('City Beauty Static Banner components initialized successfully');
    }

    /**
     * Initialize lazy loading for the section
     */
    function initLazyLoading() {
        const sections = document.querySelectorAll(staticBannerConfig.bannerSelector);
        
        if (sections.length === 0) {
            return;
        }

        // Check if Intersection Observer is supported
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        loadSection(entry.target);
                        observer.unobserve(entry.target);
                    }
                });
            }, {
                root: null,
                rootMargin: '50px',
                threshold: 0.1
            });

            sections.forEach(section => {
                observer.observe(section);
            });
        } else {
            // Fallback for browsers without Intersection Observer
            sections.forEach(section => {
                loadSection(section);
            });
        }
    }

    /**
     * Load a lazy section
     */
    function loadSection(section) {
        // Remove lazy class and add loaded class
        section.classList.remove(staticBannerConfig.lazyClass);
        section.classList.add(staticBannerConfig.loadedClass);
        
        // Trigger animation after a short delay
        setTimeout(() => {
            section.classList.add(staticBannerConfig.animateClass);
        }, 100);
        
        // Load background images
        loadBackgroundImages(section);
        
        console.log('Static Banner section loaded');
    }

    /**
     * Initialize animations
     */
    function initAnimations() {
        const sections = document.querySelectorAll(staticBannerConfig.bannerSelector);
        
        sections.forEach(section => {
            // Add animation class if section is already visible
            if (isElementInViewport(section)) {
                section.classList.add(staticBannerConfig.animateClass);
            }
        });
    }

    /**
     * Check if element is in viewport
     */
    function isElementInViewport(el) {
        const rect = el.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    /**
     * Initialize background images
     */
    function initBackgroundImages() {
        const sections = document.querySelectorAll(staticBannerConfig.bannerSelector);
        
        sections.forEach(section => {
            loadBackgroundImages(section);
        });
    }

    /**
     * Load background images for a section
     */
    function loadBackgroundImages(section) {
        const desktopImage = section.dataset.desktopImage || staticBannerConfig.defaultSettings.backgroundImage;
        const mobileImage = section.dataset.mobileImage || staticBannerConfig.defaultSettings.mobileBackgroundImage;
        const textColor = section.dataset.textColor || 'light';
        
        // Preload images
        preloadImage(desktopImage);
        preloadImage(mobileImage);
        
        // Set initial background based on screen size
        updateBackgroundImage(section, desktopImage, mobileImage);
        
        // Apply text color
        applyTextColor(section, textColor);
    }

    /**
     * Preload an image
     */
    function preloadImage(src) {
        const img = new Image();
        img.src = src;
    }

    /**
     * Update background image based on screen size
     */
    function updateBackgroundImage(section, desktopImage, mobileImage) {
        // Find the parent section element to apply background styles
        const parentSection = section.closest('.cb-static-banner');
        if (!parentSection) return;
        
        if (window.innerWidth <= 575.98) {
            // Mobile
            parentSection.style.backgroundImage = `url(${mobileImage})`;
            parentSection.style.backgroundPosition = '0px 88%';
            parentSection.style.backgroundSize = 'cover';
        } else {
            // Desktop
            parentSection.style.backgroundImage = `url(${desktopImage})`;
            parentSection.style.backgroundPosition = 'center';
            parentSection.style.backgroundSize = '100% 100%';
        }
    }

    /**
     * Apply text color based on setting
     */
    function applyTextColor(section, textColor) {
        // Find the parent section element
        const parentSection = section.closest('.cb-static-banner');
        if (!parentSection) return;
        
        // Remove existing text color classes
        parentSection.classList.remove('text-light', 'text-dark');
        
        // Add new text color class
        if (textColor === 'dark') {
            parentSection.classList.add('text-dark');
        } else {
            parentSection.classList.add('text-light');
        }
    }

    /**
     * Handle responsive behavior
     */
    function handleStaticBannerResize() {
        const sections = document.querySelectorAll(staticBannerConfig.bannerSelector);
        
        if (sections.length === 0) {
            return;
        }

        function updateResponsiveDisplay() {
            sections.forEach(section => {
                const desktopImage = section.dataset.desktopImage || staticBannerConfig.defaultSettings.backgroundImage;
                const mobileImage = section.dataset.mobileImage || staticBannerConfig.defaultSettings.mobileBackgroundImage;
                
                // Update background image
                updateBackgroundImage(section, desktopImage, mobileImage);
                
                // Padding is handled by CSS media queries, no need for inline styles
                
                // Font sizes and button styles are handled by CSS media queries
            });
        }

        // Initial call
        updateResponsiveDisplay();

        // Listen for resize events
        window.addEventListener('resize', debounce(updateResponsiveDisplay, 250));
    }

    /**
     * Debounce function to limit function calls
     */
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Initialize accessibility features
     */
    function initAccessibility() {
        const sections = document.querySelectorAll(staticBannerConfig.bannerSelector);
        
        sections.forEach(section => {
            // Add ARIA labels for screen readers
            section.setAttribute('role', 'banner');
            section.setAttribute('aria-label', 'Hero banner section');
            
            // Add ARIA labels for buttons
            const buttons = section.querySelectorAll('.btn');
            buttons.forEach(button => {
                if (!button.getAttribute('aria-label')) {
                    const buttonText = button.textContent.trim();
                    button.setAttribute('aria-label', buttonText);
                }
            });
            
            // Add ARIA labels for headings
            const headings = section.querySelectorAll('h1, h2, h3');
            headings.forEach(heading => {
                if (!heading.getAttribute('aria-label')) {
                    heading.setAttribute('aria-label', heading.textContent.trim());
                }
            });
        });
    }

    /**
     * Generate static banner HTML
     */
    function generateStaticBannerHTML(settings = {}) {
        const config = { ...staticBannerConfig.defaultSettings, ...settings };
        
        return `
            <div class="banner-content">
                <div class="banner-text">
                    ${config.subtitle ? `<p class="subtitle">${config.subtitle}</p>` : ''}
                    <div class="banner-title">
                        <h1>${config.title}</h1>
                    </div>
                    ${config.description ? `<p class="description">${config.description}</p>` : ''}
                    ${config.buttonText && config.buttonUrl ? `<a href="${config.buttonUrl}" class="btn btn-dark btn-condensed">${config.buttonText}</a>` : ''}
                </div>
            </div>
        `;
    }

    /**
     * Populate section with default content (if section is empty)
     */
    function populateStaticBannerContent() {
        const sections = document.querySelectorAll(staticBannerConfig.bannerSelector);
        
        sections.forEach(section => {
            const bannerContent = section.querySelector('.banner-content');
            
            if (!bannerContent) {
                // Generate HTML for the section
                const html = generateStaticBannerHTML();
                section.innerHTML = html;
            }
        });
    }

    /**
     * Initialize Shopify customizer support
     */
    function initCustomizerSupport() {
        // Listen for customizer changes
        if (window.Shopify && window.Shopify.designMode) {
            document.addEventListener('shopify:section:load', function(event) {
                const section = event.target.querySelector(staticBannerConfig.bannerSelector);
                if (section) {
                    loadSection(section);
                }
            });

            document.addEventListener('shopify:section:select', function(event) {
                const section = event.target.querySelector(staticBannerConfig.bannerSelector);
                if (section) {
                    // Re-apply text color when section is selected
                    const textColor = section.dataset.textColor || 'light';
                    applyTextColor(section, textColor);
                }
            });

            // Listen for setting changes in customizer
            document.addEventListener('shopify:section:reorder', function(event) {
                const sections = document.querySelectorAll(staticBannerConfig.bannerSelector);
                sections.forEach(section => {
                    const textColor = section.dataset.textColor || 'light';
                    applyTextColor(section, textColor);
                });
            });

            // Watch for data attribute changes (for real-time customizer updates)
            const sections = document.querySelectorAll(staticBannerConfig.bannerSelector);
            sections.forEach(section => {
                const observer = new MutationObserver(function(mutations) {
                    mutations.forEach(function(mutation) {
                        if (mutation.type === 'attributes' && mutation.attributeName === 'data-text-color') {
                            const textColor = section.dataset.textColor || 'light';
                            applyTextColor(section, textColor);
                        }
                    });
                });

                observer.observe(section, {
                    attributes: true,
                    attributeFilter: ['data-text-color']
                });
            });

            // Listen for customizer setting changes
            document.addEventListener('shopify:section:setting:change', function(event) {
                if (event.detail.settingId === 'text_color') {
                    const section = event.target.querySelector(staticBannerConfig.bannerSelector);
                    if (section) {
                        const textColor = event.detail.value || 'light';
                        applyTextColor(section, textColor);
                    }
                }
            });
        }
    }

    /**
     * Initialize all components
     */
    function initAll() {
        initCityBeautyStaticBannerComponents();
        initAccessibility();
        populateStaticBannerContent();
        initCustomizerSupport();
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAll);
    } else {
        initAll();
    }

    // Export functions for external use
    window.CityBeautyStaticBanner = {
        init: initCityBeautyStaticBannerComponents,
        loadSection: loadSection,
        handleResize: handleStaticBannerResize,
        initLazyLoading: initLazyLoading,
        initAnimations: initAnimations,
        initBackgroundImages: initBackgroundImages,
        populateStaticBannerContent: populateStaticBannerContent,
        generateHTML: generateStaticBannerHTML
    };

})();
